// frontend/src/components/cash-close/VarianceTableDetail.js
import React from 'react';
import { useTheme, sans, mono, red, green } from '../../tokens';
import { tintFor } from './VarianceCell';

const fmtCAD = (n) => {
  if (n == null) return '—';
  const sign = n < 0 ? '-' : '';
  return `${sign}$${Math.abs(n).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
};

export default function VarianceTableDetail({ rows, priorMonthSummary, currency, fxRate, onCellClick, expandedDept, drilldownRenderer, commentary }) {
  const t = useTheme();
  const conv = (n) => currency === 'USD' && n != null ? n / fxRate : n;

  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: sans, fontSize: 12 }}>
      <thead>
        <tr style={{ borderBottom: `1px solid ${t.border}` }}>
          <th style={th(t)}>Department</th>
          <th style={th(t, 'right')}>Actual</th>
          <th style={th(t, 'right')}>Forecast</th>
          <th style={th(t, 'right')}>Variance $</th>
          <th style={th(t, 'right')}>Variance %</th>
          <th style={th(t, 'right')}>Δ vs prior mo.</th>
          <th style={th(t)}>Notes</th>
          <th style={{ width: 24 }} />
        </tr>
      </thead>
      <tbody>
        {rows.map(({ dept, cells }) => {
          const c = cells[0];
          const tint = tintFor(c.varPct);
          const note = commentary?.[dept]?.comment || '';
          const priorActual = priorMonthSummary?.view_a?.[dept];
          const delta = priorActual != null && c.actual != null ? c.actual - priorActual : null;
          const deltaPct = priorActual != null && priorActual !== 0 && c.actual != null
            ? ((Math.abs(c.actual) - Math.abs(priorActual)) / Math.abs(priorActual)) * 100
            : null;
          return (
            <React.Fragment key={dept}>
              <tr onClick={() => onCellClick(dept, c.month)}
                  style={{ borderBottom: `1px solid ${t.borderLight}`, cursor: 'pointer', background: tint || 'transparent' }}>
                <td style={td(t)}>{dept}</td>
                <td style={td(t, 'right', mono)}>{fmtCAD(conv(c.actual))}</td>
                <td style={td(t, 'right', mono, t.textSecondary)}>{fmtCAD(conv(c.forecast))}</td>
                <td style={td(t, 'right', mono, c.varPct > 0 ? red : c.varPct < 0 ? green : t.textSecondary)}>
                  {c.varAmt == null ? '—' : (c.varAmt < 0 ? '' : '+') + fmtCAD(conv(c.varAmt))}
                </td>
                <td style={td(t, 'right', mono, c.varPct > 0 ? red : c.varPct < 0 ? green : t.textSecondary, 600)}>
                  {c.varPct == null ? '—' : `${c.varPct > 0 ? '+' : ''}${c.varPct.toFixed(0)}%`}
                </td>
                <td style={td(t, 'right', mono, delta == null ? t.textMuted : delta > 0 ? red : green)}>
                  {delta == null ? '—' : `${delta < 0 ? '' : '+'}${fmtCAD(conv(delta))} · ${deltaPct > 0 ? '+' : ''}${deltaPct?.toFixed(0) ?? '—'}%`}
                </td>
                <td style={{ ...td(t), maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontStyle: note ? 'normal' : 'italic', color: note ? t.textPrimary : t.textMuted }} title={note}>
                  {note || '—'}
                </td>
                <td style={{ textAlign: 'center', color: t.textMuted, fontSize: 10 }}>{expandedDept === dept ? '▾' : '▸'}</td>
              </tr>
              {expandedDept === dept && (
                <tr><td colSpan={8} style={{ padding: 0 }}>{drilldownRenderer(dept, c.month)}</td></tr>
              )}
            </React.Fragment>
          );
        })}
      </tbody>
    </table>
  );
}

const th = (t, align = 'left') => ({
  textAlign: align, padding: '8px 6px', fontSize: 10, fontWeight: 600, color: t.textMuted,
  textTransform: 'uppercase', letterSpacing: 0.5,
});
const td = (t, align = 'left', font = sans, color = null, weight = 400) => ({
  textAlign: align, padding: '8px 6px', color: color || t.textPrimary, fontFamily: font, fontWeight: weight,
});
